import { FaceIconBrandLarge } from "@/theme/icons";
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  RadioGroup,
  Stack,
  Typography,
} from "@mui/material";
import { Trans, useTranslation } from "react-i18next";
import { HealthScoreQuestionnaireProps, QuestionType } from "./types";
import { convertPxToRem } from "@/utils";
import { useQuestionnaireState } from "./useQuestionnaireState";
import QuestionnaireHeader from "./components/QuestionnaireHeader";
import QuestionOption from "./components/QuestionOption";
import QuestionnaireNavigation from "./components/QuestionnaireNavigation";

/** Converts PascalCase question type to translation key (snake_case), e.g. IntenseExercise -> intense_exercise */
function toTranslationKey(type: string): string {
  return type
    .replace(/([A-Z])/g, "_$1")
    .toLowerCase()
    .replace(/^_/, "");
}

export default function HealthScoreQuestionnaire({
  questions,
  translationPrefix,
  onSubmit,
  onClose,
  initialAnswers = {},
  title,
}: HealthScoreQuestionnaireProps) {
  const { t } = useTranslation();

  const {
    topRef,
    currentQuestionIndex,
    currentQuestion,
    selectedOption,
    selectedOptions,
    strengthTraining,
    isSubmitting,
    handleNext,
    handlePrevious,
    handleOptionChange,
    handleCheckboxChange,
    handleStrengthTrainingChange,
    handleClose,
  } = useQuestionnaireState({
    questions,
    initialAnswers,
    onSubmit,
    onClose,
  });

  return (
    <Stack
      ref={topRef}
      p={2}
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
      aria-label={title}
    >
      <QuestionnaireHeader
        showBackButton={currentQuestionIndex > 0}
        onBack={handlePrevious}
        onClose={handleClose}
      />

      <Box marginY={4}>
        <FaceIconBrandLarge />
      </Box>

      <Typography variant="h3" marginBottom={2.5} lineHeight="150%">
        <Trans
          i18nKey={
            `${translationPrefix}.${toTranslationKey(currentQuestion.type)}` as any
          }
        />
      </Typography>

      {currentQuestion.meta?.description && (
        <Typography variant="body1" marginBottom={2.5} color="text.secondary">
          {t(
            `${translationPrefix}.${toTranslationKey(currentQuestion.type)}.description` as any
          )}
        </Typography>
      )}

      <FormControl
        component="fieldset"
        sx={{
          paddingBottom: convertPxToRem(40),
          flex: 1,
          "& .MuiFormControlLabel-label": {
            fontSize: convertPxToRem(20),
          },
          "& .MuiFormControlLabel-label span": {
            fontSize: convertPxToRem(16),
            color: "neutral.main",
            display: "block",
          },
        }}
      >
        <Box
          component="legend"
          sx={{
            position: "absolute",
            width: 1,
            height: 1,
            overflow: "hidden",
            clip: "rect(0,0,0,0)",
          }}
        >
          <Trans
            i18nKey={
              `${translationPrefix}.${toTranslationKey(currentQuestion.type)}` as any
            }
          />
        </Box>
        {currentQuestion.multiSelect ? (
          <FormGroup>
            {(
              currentQuestion.meta?.values ||
              currentQuestion.options ||
              []
            ).map((item, index) => {
              const value = typeof item === "string" ? item : item.value;
              const baseKey = `${translationPrefix}.${toTranslationKey(currentQuestion.type)}`;
              const optionTranslationKey =
                (typeof item === "object" && item.translationKey) ||
                `${baseKey}.option.${value.toLowerCase()}`;

              return (
                <FormControlLabel
                  key={index}
                  sx={{
                    marginTop: 2,
                    alignItems: "flex-start",
                    "& .MuiFormControlLabel-label": {
                      paddingTop: 0.75,
                    },
                  }}
                  control={
                    <Checkbox
                      checked={selectedOptions.includes(value)}
                      onChange={(e) =>
                        handleCheckboxChange(value, e.target.checked)
                      }
                      sx={{
                        "&:not(.Mui-checked) .MuiSvgIcon-root": {
                          color: "primary.100",
                        },
                      }}
                    />
                  }
                  label={
                    <Trans
                      i18nKey={optionTranslationKey as any}
                      components={{
                        strong: <strong />,
                        span: <span />,
                      }}
                    />
                  }
                />
              );
            })}
          </FormGroup>
        ) : (
          <RadioGroup value={selectedOption} onChange={handleOptionChange}>
            {(
              currentQuestion.meta?.values ||
              currentQuestion.options ||
              []
            ).map((item, index) => {
              const value = typeof item === "string" ? item : item.value;
              const baseKey = `${translationPrefix}.${toTranslationKey(currentQuestion.type)}`;
              const optionTranslationKey =
                (typeof item === "object" && item.translationKey) ||
                `${baseKey}.option.${value.toLowerCase()}`;
              const showCheckbox =
                currentQuestion.type === QuestionType.INTENSE_EXERCISE &&
                index === 1;

              return (
                <QuestionOption
                  key={index}
                  value={value}
                  translationKey={optionTranslationKey}
                  isSelected={selectedOption === value}
                  showCheckbox={showCheckbox}
                  strengthTrainingChecked={strengthTraining}
                  onStrengthTrainingChange={handleStrengthTrainingChange}
                />
              );
            })}
          </RadioGroup>
        )}
      </FormControl>

      <QuestionnaireNavigation
        onNext={handleNext}
        isDisabled={
          currentQuestion.multiSelect
            ? selectedOptions.length === 0 || isSubmitting
            : !selectedOption || isSubmitting
        }
        isSubmitting={isSubmitting}
        isLastQuestion={currentQuestionIndex === questions.length - 1}
        buttonText={{
          submitting: t("HealthScore.questionnaire.common.button.submitting"),
          submit: t("HealthScore.questionnaire.common.button.submit"),
          next: t("HealthScore.questionnaire.common.button.next"),
        }}
      />
    </Stack>
  );
}
